/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import * as runtime from '../runtime';
import type {
  InvestorProductDocument,
  PaginatedDropdownElementList,
  PaginatedInvestorProductDocumentList,
  PureDocumentType,
} from '../models/index';
import {
    InvestorProductDocumentFromJSON,
    InvestorProductDocumentToJSON,
    PaginatedDropdownElementListFromJSON,
    PaginatedDropdownElementListToJSON,
    PaginatedInvestorProductDocumentListFromJSON,
    PaginatedInvestorProductDocumentListToJSON,
    PureDocumentTypeFromJSON,
    PureDocumentTypeToJSON,
} from '../models/index';

export interface AdminInvestorProductDocumentsCreateRequest {
    productId: string;
    file: Blob;
    documentType?: PureDocumentType;
}

export interface AdminInvestorProductDocumentsDestroyRequest {
    id: string;
}

export interface AdminInvestorProductDocumentsDropdownListRequest {
    createdAtAfter?: Date;
    createdAtBefore?: Date;
    documentName?: string;
    documentType?: AdminInvestorProductDocumentsDropdownListDocumentTypeEnum;
    field?: AdminInvestorProductDocumentsDropdownListFieldEnum;
    id?: string;
    investorId?: string;
    investorName?: string;
    limit?: number;
    offset?: number;
    ordering?: AdminInvestorProductDocumentsDropdownListOrderingEnum;
    productId?: string;
    productName?: string;
    search?: string;
    updatedAtAfter?: Date;
    updatedAtBefore?: Date;
}

export interface AdminInvestorProductDocumentsListRequest {
    createdAtAfter?: Date;
    createdAtBefore?: Date;
    documentName?: Array<string>;
    documentType?: AdminInvestorProductDocumentsListDocumentTypeEnum;
    id?: Array<string>;
    investorId?: Array<string>;
    investorName?: Array<string>;
    limit?: number;
    offset?: number;
    ordering?: AdminInvestorProductDocumentsListOrderingEnum;
    productId?: string;
    productName?: Array<string>;
    search?: string;
    updatedAtAfter?: Date;
    updatedAtBefore?: Date;
}

export interface AdminInvestorProductDocumentsValuesRetrieveRequest {
    field: AdminInvestorProductDocumentsValuesRetrieveFieldEnum;
    createdAtAfter?: Date;
    createdAtBefore?: Date;
    documentName?: string;
    documentType?: AdminInvestorProductDocumentsValuesRetrieveDocumentTypeEnum;
    icontains?: string;
    id?: string;
    investorId?: string;
    investorName?: string;
    ordering?: AdminInvestorProductDocumentsValuesRetrieveOrderingEnum;
    productId?: string;
    productName?: string;
    search?: string;
    updatedAtAfter?: Date;
    updatedAtBefore?: Date;
}

/**
 * 
 */
export class InvestorProductDocumentsApi extends runtime.BaseAPI {

    /**
     * Can be used to upload investor-product documents. Files are uploaded via HTTP multipart, see the [documentation](https://swagger.io/docs/specification/describing-request-body/file-upload/#multipart).
     * Create
     */
    async adminInvestorProductDocumentsCreateRaw(requestParameters: AdminInvestorProductDocumentsCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<InvestorProductDocument>> {
        if (requestParameters['productId'] == null) {
            throw new runtime.RequiredError(
                'productId',
                'Required parameter "productId" was null or undefined when calling adminInvestorProductDocumentsCreate().'
            );
        }

        if (requestParameters['file'] == null) {
            throw new runtime.RequiredError(
                'file',
                'Required parameter "file" was null or undefined when calling adminInvestorProductDocumentsCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const consumes: runtime.Consume[] = [
            { contentType: 'multipart/form-data' },
        ];
        // @ts-ignore: canConsumeForm may be unused
        const canConsumeForm = runtime.canConsumeForm(consumes);

        let formParams: { append(param: string, value: any): any };
        let useForm = false;
        // use FormData to transmit files using content-type "multipart/form-data"
        useForm = canConsumeForm;
        if (useForm) {
            formParams = new FormData();
        } else {
            formParams = new URLSearchParams();
        }

        if (requestParameters['productId'] != null) {
            formParams.append('product_id', requestParameters['productId'] as any);
        }

        if (requestParameters['documentType'] != null) {
            formParams.append('document_type', requestParameters['documentType'] as any);
        }

        if (requestParameters['file'] != null) {
            formParams.append('file', requestParameters['file'] as any);
        }

        const response = await this.request({
            path: `/api/admin/investor-product-documents/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: formParams,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => InvestorProductDocumentFromJSON(jsonValue));
    }

    /**
     * Can be used to upload investor-product documents. Files are uploaded via HTTP multipart, see the [documentation](https://swagger.io/docs/specification/describing-request-body/file-upload/#multipart).
     * Create
     */
    async adminInvestorProductDocumentsCreate(requestParameters: AdminInvestorProductDocumentsCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<InvestorProductDocument> {
        const response = await this.adminInvestorProductDocumentsCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Delete the given InvestorProductDocument object.
     */
    async adminInvestorProductDocumentsDestroyRaw(requestParameters: AdminInvestorProductDocumentsDestroyRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling adminInvestorProductDocumentsDestroy().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/admin/investor-product-documents/{id}/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'DELETE',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Delete the given InvestorProductDocument object.
     */
    async adminInvestorProductDocumentsDestroy(requestParameters: AdminInvestorProductDocumentsDestroyRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.adminInvestorProductDocumentsDestroyRaw(requestParameters, initOverrides);
    }

    /**
     * Get dropdown elements with IDs and names.
     * Get dropdown elements
     */
    async adminInvestorProductDocumentsDropdownListRaw(requestParameters: AdminInvestorProductDocumentsDropdownListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedDropdownElementList>> {
        const queryParameters: any = {};

        if (requestParameters['createdAtAfter'] != null) {
            queryParameters['created_at_after'] = (requestParameters['createdAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['createdAtBefore'] != null) {
            queryParameters['created_at_before'] = (requestParameters['createdAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['documentName'] != null) {
            queryParameters['document_name'] = requestParameters['documentName'];
        }

        if (requestParameters['documentType'] != null) {
            queryParameters['document_type'] = requestParameters['documentType'];
        }

        if (requestParameters['field'] != null) {
            queryParameters['field'] = requestParameters['field'];
        }

        if (requestParameters['id'] != null) {
            queryParameters['id'] = requestParameters['id'];
        }

        if (requestParameters['investorId'] != null) {
            queryParameters['investor_id'] = requestParameters['investorId'];
        }

        if (requestParameters['investorName'] != null) {
            queryParameters['investor_name'] = requestParameters['investorName'];
        }

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        if (requestParameters['productId'] != null) {
            queryParameters['product_id'] = requestParameters['productId'];
        }

        if (requestParameters['productName'] != null) {
            queryParameters['product_name'] = requestParameters['productName'];
        }

        if (requestParameters['search'] != null) {
            queryParameters['search'] = requestParameters['search'];
        }

        if (requestParameters['updatedAtAfter'] != null) {
            queryParameters['updated_at_after'] = (requestParameters['updatedAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['updatedAtBefore'] != null) {
            queryParameters['updated_at_before'] = (requestParameters['updatedAtBefore'] as any).toISOString().substring(0,10);
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/admin/investor-product-documents/dropdown/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedDropdownElementListFromJSON(jsonValue));
    }

    /**
     * Get dropdown elements with IDs and names.
     * Get dropdown elements
     */
    async adminInvestorProductDocumentsDropdownList(requestParameters: AdminInvestorProductDocumentsDropdownListRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedDropdownElementList> {
        const response = await this.adminInvestorProductDocumentsDropdownListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * List InvestorProductDocuments.
     */
    async adminInvestorProductDocumentsListRaw(requestParameters: AdminInvestorProductDocumentsListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedInvestorProductDocumentList>> {
        const queryParameters: any = {};

        if (requestParameters['createdAtAfter'] != null) {
            queryParameters['created_at_after'] = (requestParameters['createdAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['createdAtBefore'] != null) {
            queryParameters['created_at_before'] = (requestParameters['createdAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['documentName'] != null) {
            queryParameters['document_name'] = requestParameters['documentName'];
        }

        if (requestParameters['documentType'] != null) {
            queryParameters['document_type'] = requestParameters['documentType'];
        }

        if (requestParameters['id'] != null) {
            queryParameters['id'] = requestParameters['id'];
        }

        if (requestParameters['investorId'] != null) {
            queryParameters['investor_id'] = requestParameters['investorId'];
        }

        if (requestParameters['investorName'] != null) {
            queryParameters['investor_name'] = requestParameters['investorName'];
        }

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        if (requestParameters['productId'] != null) {
            queryParameters['product_id'] = requestParameters['productId'];
        }

        if (requestParameters['productName'] != null) {
            queryParameters['product_name'] = requestParameters['productName'];
        }

        if (requestParameters['search'] != null) {
            queryParameters['search'] = requestParameters['search'];
        }

        if (requestParameters['updatedAtAfter'] != null) {
            queryParameters['updated_at_after'] = (requestParameters['updatedAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['updatedAtBefore'] != null) {
            queryParameters['updated_at_before'] = (requestParameters['updatedAtBefore'] as any).toISOString().substring(0,10);
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/admin/investor-product-documents/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedInvestorProductDocumentListFromJSON(jsonValue));
    }

    /**
     * List InvestorProductDocuments.
     */
    async adminInvestorProductDocumentsList(requestParameters: AdminInvestorProductDocumentsListRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedInvestorProductDocumentList> {
        const response = await this.adminInvestorProductDocumentsListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Get distinct string values for a column - no pagination, capped at 50 elements.
     * Get the names one column can have
     */
    async adminInvestorProductDocumentsValuesRetrieveRaw(requestParameters: AdminInvestorProductDocumentsValuesRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<string>>> {
        if (requestParameters['field'] == null) {
            throw new runtime.RequiredError(
                'field',
                'Required parameter "field" was null or undefined when calling adminInvestorProductDocumentsValuesRetrieve().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['createdAtAfter'] != null) {
            queryParameters['created_at_after'] = (requestParameters['createdAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['createdAtBefore'] != null) {
            queryParameters['created_at_before'] = (requestParameters['createdAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['documentName'] != null) {
            queryParameters['document_name'] = requestParameters['documentName'];
        }

        if (requestParameters['documentType'] != null) {
            queryParameters['document_type'] = requestParameters['documentType'];
        }

        if (requestParameters['field'] != null) {
            queryParameters['field'] = requestParameters['field'];
        }

        if (requestParameters['icontains'] != null) {
            queryParameters['icontains'] = requestParameters['icontains'];
        }

        if (requestParameters['id'] != null) {
            queryParameters['id'] = requestParameters['id'];
        }

        if (requestParameters['investorId'] != null) {
            queryParameters['investor_id'] = requestParameters['investorId'];
        }

        if (requestParameters['investorName'] != null) {
            queryParameters['investor_name'] = requestParameters['investorName'];
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        if (requestParameters['productId'] != null) {
            queryParameters['product_id'] = requestParameters['productId'];
        }

        if (requestParameters['productName'] != null) {
            queryParameters['product_name'] = requestParameters['productName'];
        }

        if (requestParameters['search'] != null) {
            queryParameters['search'] = requestParameters['search'];
        }

        if (requestParameters['updatedAtAfter'] != null) {
            queryParameters['updated_at_after'] = (requestParameters['updatedAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['updatedAtBefore'] != null) {
            queryParameters['updated_at_before'] = (requestParameters['updatedAtBefore'] as any).toISOString().substring(0,10);
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/admin/investor-product-documents/values/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse<any>(response);
    }

    /**
     * Get distinct string values for a column - no pagination, capped at 50 elements.
     * Get the names one column can have
     */
    async adminInvestorProductDocumentsValuesRetrieve(requestParameters: AdminInvestorProductDocumentsValuesRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<string>> {
        const response = await this.adminInvestorProductDocumentsValuesRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

}

/**
 * @export
 */
export const AdminInvestorProductDocumentsDropdownListDocumentTypeEnum = {
    BIB: 'BIB',
    CASHLINK_TOS: 'CASHLINK_TOS',
    COMMERCIAL_REGISTER_STATEMENT: 'COMMERCIAL_REGISTER_STATEMENT',
    EFFECTA_ACCEPTANCE_DELAY: 'EFFECTA_ACCEPTANCE_DELAY',
    EFFECTA_AUSFUEHRUNGSGRUNDSAETZE: 'EFFECTA_AUSFUEHRUNGSGRUNDSAETZE',
    EFFECTA_BIB: 'EFFECTA_BIB',
    EFFECTA_COMMERCIAL_REGISTER_STATEMENT: 'EFFECTA_COMMERCIAL_REGISTER_STATEMENT',
    EFFECTA_CONSUMER_INFO: 'EFFECTA_CONSUMER_INFO',
    EFFECTA_COSTS: 'EFFECTA_COSTS',
    EFFECTA_COSTS_EFFECTA_EX_ANTE: 'EFFECTA_COSTS_EFFECTA_EX_ANTE',
    EFFECTA_COSTS_EFFECTA_EX_POST: 'EFFECTA_COSTS_EFFECTA_EX_POST',
    EFFECTA_CRYPTOREGISTRY_TOS: 'EFFECTA_CRYPTOREGISTRY_TOS',
    EFFECTA_CUSTOMER_INFO: 'EFFECTA_CUSTOMER_INFO',
    EFFECTA_DOCUMENT: 'EFFECTA_DOCUMENT',
    EFFECTA_INVESTOR_BROCHURE: 'EFFECTA_INVESTOR_BROCHURE',
    EFFECTA_LEGITIMATION: 'EFFECTA_LEGITIMATION',
    EFFECTA_LIST_OF_SHAREHOLDERS: 'EFFECTA_LIST_OF_SHAREHOLDERS',
    EFFECTA_LOAN_CONDITIONS: 'EFFECTA_LOAN_CONDITIONS',
    EFFECTA_NON_ASSESSMENT_CERTIFICATE: 'EFFECTA_NON_ASSESSMENT_CERTIFICATE',
    EFFECTA_PARTICIPATION_RIGHT_CONDITIONS: 'EFFECTA_PARTICIPATION_RIGHT_CONDITIONS',
    EFFECTA_PROFESSIONAL_INVESTOR_CLASSIFICATION: 'EFFECTA_PROFESSIONAL_INVESTOR_CLASSIFICATION',
    EFFECTA_PROFESSIONAL_INVESTOR_OMISSION_WPHG: 'EFFECTA_PROFESSIONAL_INVESTOR_OMISSION_WPHG',
    EFFECTA_SELF_DISCLOSURE: 'EFFECTA_SELF_DISCLOSURE',
    EFFECTA_TERMS_AND_CONDITIONS: 'EFFECTA_TERMS_AND_CONDITIONS',
    EFFECTA_TOS: 'EFFECTA_TOS',
    EFFECTA_TRANSPARANCY_REGISTER: 'EFFECTA_TRANSPARANCY_REGISTER',
    EFFECTA_UNSUITABILITY: 'EFFECTA_UNSUITABILITY',
    EFFECTA_VIB: 'EFFECTA_VIB',
    EFFECTA_WAIVER: 'EFFECTA_WAIVER',
    EFFECTA_WIB: 'EFFECTA_WIB',
    EFFECTA_ZEICHNUNGSSCHEIN: 'EFFECTA_ZEICHNUNGSSCHEIN',
    EMAIL: 'EMAIL',
    EWPG_REGISTRY_STATEMENT: 'EWPG_REGISTRY_STATEMENT',
    IMAGE: 'IMAGE',
    INCOME_STATEMENT: 'INCOME_STATEMENT',
    INVESTMENT_AGREEMENT: 'INVESTMENT_AGREEMENT',
    LEGITIMATION_PROTOCOL: 'LEGITIMATION_PROTOCOL',
    LIMITED_PARTNERSHIP_AGREEMENT: 'LIMITED_PARTNERSHIP_AGREEMENT',
    NOT_SPECIFIED: 'NOT_SPECIFIED',
    OTHER: 'OTHER',
    PAYMENT_AGB: 'PAYMENT_AGB',
    QUESTIONNAIRE: 'QUESTIONNAIRE',
    QUESTIONNAIRE_WPHG: 'QUESTIONNAIRE_WPHG',
    RECEIPT_SECONDARY_MARKET: 'RECEIPT_SECONDARY_MARKET',
    SECURITY_PROSPECTUS: 'SECURITY_PROSPECTUS',
    SELF_DISCLOSURE: 'SELF_DISCLOSURE',
    SHAREHOLDERS: 'SHAREHOLDERS',
    SURVEY_SUMMARY: 'SURVEY_SUMMARY',
    TAX_STATEMENT: 'TAX_STATEMENT',
    TRADE_COPY: 'TRADE_COPY',
    UNSUITABILITY: 'UNSUITABILITY',
    VIB: 'VIB',
    WAIVER: 'WAIVER',
    WIB: 'WIB'
} as const;
export type AdminInvestorProductDocumentsDropdownListDocumentTypeEnum = typeof AdminInvestorProductDocumentsDropdownListDocumentTypeEnum[keyof typeof AdminInvestorProductDocumentsDropdownListDocumentTypeEnum];
/**
 * @export
 */
export const AdminInvestorProductDocumentsDropdownListFieldEnum = {
    DOCUMENT_NAME: 'document_name',
    DOCUMENT_TYPE: 'document_type',
    INVESTOR_ID: 'investor_id',
    INVESTOR_NAME: 'investor_name',
    PRODUCT_ID: 'product_id',
    PRODUCT_NAME: 'product_name'
} as const;
export type AdminInvestorProductDocumentsDropdownListFieldEnum = typeof AdminInvestorProductDocumentsDropdownListFieldEnum[keyof typeof AdminInvestorProductDocumentsDropdownListFieldEnum];
/**
 * @export
 */
export const AdminInvestorProductDocumentsDropdownListOrderingEnum = {
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    DOCUMENT_NAME_DESC: '-document_name',
    DOCUMENT_NAME_ASC: 'document_name',
    DOCUMENT_TYPE_DESC: '-document_type',
    DOCUMENT_TYPE_ASC: 'document_type',
    ID_DESC: '-id',
    ID_ASC: 'id',
    INVESTOR_ID_DESC: '-investor_id',
    INVESTOR_ID_ASC: 'investor_id',
    INVESTOR_NAME_DESC: '-investor_name',
    INVESTOR_NAME_ASC: 'investor_name',
    PRODUCT_ID_DESC: '-product_id',
    PRODUCT_ID_ASC: 'product_id',
    PRODUCT_NAME_DESC: '-product_name',
    PRODUCT_NAME_ASC: 'product_name',
    UPDATED_AT_DESC: '-updated_at',
    UPDATED_AT_ASC: 'updated_at'
} as const;
export type AdminInvestorProductDocumentsDropdownListOrderingEnum = typeof AdminInvestorProductDocumentsDropdownListOrderingEnum[keyof typeof AdminInvestorProductDocumentsDropdownListOrderingEnum];
/**
 * @export
 */
export const AdminInvestorProductDocumentsListDocumentTypeEnum = {
    BIB: 'BIB',
    CASHLINK_TOS: 'CASHLINK_TOS',
    COMMERCIAL_REGISTER_STATEMENT: 'COMMERCIAL_REGISTER_STATEMENT',
    EFFECTA_ACCEPTANCE_DELAY: 'EFFECTA_ACCEPTANCE_DELAY',
    EFFECTA_AUSFUEHRUNGSGRUNDSAETZE: 'EFFECTA_AUSFUEHRUNGSGRUNDSAETZE',
    EFFECTA_BIB: 'EFFECTA_BIB',
    EFFECTA_COMMERCIAL_REGISTER_STATEMENT: 'EFFECTA_COMMERCIAL_REGISTER_STATEMENT',
    EFFECTA_CONSUMER_INFO: 'EFFECTA_CONSUMER_INFO',
    EFFECTA_COSTS: 'EFFECTA_COSTS',
    EFFECTA_COSTS_EFFECTA_EX_ANTE: 'EFFECTA_COSTS_EFFECTA_EX_ANTE',
    EFFECTA_COSTS_EFFECTA_EX_POST: 'EFFECTA_COSTS_EFFECTA_EX_POST',
    EFFECTA_CRYPTOREGISTRY_TOS: 'EFFECTA_CRYPTOREGISTRY_TOS',
    EFFECTA_CUSTOMER_INFO: 'EFFECTA_CUSTOMER_INFO',
    EFFECTA_DOCUMENT: 'EFFECTA_DOCUMENT',
    EFFECTA_INVESTOR_BROCHURE: 'EFFECTA_INVESTOR_BROCHURE',
    EFFECTA_LEGITIMATION: 'EFFECTA_LEGITIMATION',
    EFFECTA_LIST_OF_SHAREHOLDERS: 'EFFECTA_LIST_OF_SHAREHOLDERS',
    EFFECTA_LOAN_CONDITIONS: 'EFFECTA_LOAN_CONDITIONS',
    EFFECTA_NON_ASSESSMENT_CERTIFICATE: 'EFFECTA_NON_ASSESSMENT_CERTIFICATE',
    EFFECTA_PARTICIPATION_RIGHT_CONDITIONS: 'EFFECTA_PARTICIPATION_RIGHT_CONDITIONS',
    EFFECTA_PROFESSIONAL_INVESTOR_CLASSIFICATION: 'EFFECTA_PROFESSIONAL_INVESTOR_CLASSIFICATION',
    EFFECTA_PROFESSIONAL_INVESTOR_OMISSION_WPHG: 'EFFECTA_PROFESSIONAL_INVESTOR_OMISSION_WPHG',
    EFFECTA_SELF_DISCLOSURE: 'EFFECTA_SELF_DISCLOSURE',
    EFFECTA_TERMS_AND_CONDITIONS: 'EFFECTA_TERMS_AND_CONDITIONS',
    EFFECTA_TOS: 'EFFECTA_TOS',
    EFFECTA_TRANSPARANCY_REGISTER: 'EFFECTA_TRANSPARANCY_REGISTER',
    EFFECTA_UNSUITABILITY: 'EFFECTA_UNSUITABILITY',
    EFFECTA_VIB: 'EFFECTA_VIB',
    EFFECTA_WAIVER: 'EFFECTA_WAIVER',
    EFFECTA_WIB: 'EFFECTA_WIB',
    EFFECTA_ZEICHNUNGSSCHEIN: 'EFFECTA_ZEICHNUNGSSCHEIN',
    EMAIL: 'EMAIL',
    EWPG_REGISTRY_STATEMENT: 'EWPG_REGISTRY_STATEMENT',
    IMAGE: 'IMAGE',
    INCOME_STATEMENT: 'INCOME_STATEMENT',
    INVESTMENT_AGREEMENT: 'INVESTMENT_AGREEMENT',
    LEGITIMATION_PROTOCOL: 'LEGITIMATION_PROTOCOL',
    LIMITED_PARTNERSHIP_AGREEMENT: 'LIMITED_PARTNERSHIP_AGREEMENT',
    NOT_SPECIFIED: 'NOT_SPECIFIED',
    OTHER: 'OTHER',
    PAYMENT_AGB: 'PAYMENT_AGB',
    QUESTIONNAIRE: 'QUESTIONNAIRE',
    QUESTIONNAIRE_WPHG: 'QUESTIONNAIRE_WPHG',
    RECEIPT_SECONDARY_MARKET: 'RECEIPT_SECONDARY_MARKET',
    SECURITY_PROSPECTUS: 'SECURITY_PROSPECTUS',
    SELF_DISCLOSURE: 'SELF_DISCLOSURE',
    SHAREHOLDERS: 'SHAREHOLDERS',
    SURVEY_SUMMARY: 'SURVEY_SUMMARY',
    TAX_STATEMENT: 'TAX_STATEMENT',
    TRADE_COPY: 'TRADE_COPY',
    UNSUITABILITY: 'UNSUITABILITY',
    VIB: 'VIB',
    WAIVER: 'WAIVER',
    WIB: 'WIB'
} as const;
export type AdminInvestorProductDocumentsListDocumentTypeEnum = typeof AdminInvestorProductDocumentsListDocumentTypeEnum[keyof typeof AdminInvestorProductDocumentsListDocumentTypeEnum];
/**
 * @export
 */
export const AdminInvestorProductDocumentsListOrderingEnum = {
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    DOCUMENT_NAME_DESC: '-document_name',
    DOCUMENT_NAME_ASC: 'document_name',
    DOCUMENT_TYPE_DESC: '-document_type',
    DOCUMENT_TYPE_ASC: 'document_type',
    ID_DESC: '-id',
    ID_ASC: 'id',
    INVESTOR_ID_DESC: '-investor_id',
    INVESTOR_ID_ASC: 'investor_id',
    INVESTOR_NAME_DESC: '-investor_name',
    INVESTOR_NAME_ASC: 'investor_name',
    PRODUCT_ID_DESC: '-product_id',
    PRODUCT_ID_ASC: 'product_id',
    PRODUCT_NAME_DESC: '-product_name',
    PRODUCT_NAME_ASC: 'product_name',
    UPDATED_AT_DESC: '-updated_at',
    UPDATED_AT_ASC: 'updated_at'
} as const;
export type AdminInvestorProductDocumentsListOrderingEnum = typeof AdminInvestorProductDocumentsListOrderingEnum[keyof typeof AdminInvestorProductDocumentsListOrderingEnum];
/**
 * @export
 */
export const AdminInvestorProductDocumentsValuesRetrieveFieldEnum = {
    DOCUMENT_NAME: 'document_name',
    DOCUMENT_TYPE: 'document_type',
    INVESTOR_ID: 'investor_id',
    INVESTOR_NAME: 'investor_name',
    PRODUCT_ID: 'product_id',
    PRODUCT_NAME: 'product_name'
} as const;
export type AdminInvestorProductDocumentsValuesRetrieveFieldEnum = typeof AdminInvestorProductDocumentsValuesRetrieveFieldEnum[keyof typeof AdminInvestorProductDocumentsValuesRetrieveFieldEnum];
/**
 * @export
 */
export const AdminInvestorProductDocumentsValuesRetrieveDocumentTypeEnum = {
    BIB: 'BIB',
    CASHLINK_TOS: 'CASHLINK_TOS',
    COMMERCIAL_REGISTER_STATEMENT: 'COMMERCIAL_REGISTER_STATEMENT',
    EFFECTA_ACCEPTANCE_DELAY: 'EFFECTA_ACCEPTANCE_DELAY',
    EFFECTA_AUSFUEHRUNGSGRUNDSAETZE: 'EFFECTA_AUSFUEHRUNGSGRUNDSAETZE',
    EFFECTA_BIB: 'EFFECTA_BIB',
    EFFECTA_COMMERCIAL_REGISTER_STATEMENT: 'EFFECTA_COMMERCIAL_REGISTER_STATEMENT',
    EFFECTA_CONSUMER_INFO: 'EFFECTA_CONSUMER_INFO',
    EFFECTA_COSTS: 'EFFECTA_COSTS',
    EFFECTA_COSTS_EFFECTA_EX_ANTE: 'EFFECTA_COSTS_EFFECTA_EX_ANTE',
    EFFECTA_COSTS_EFFECTA_EX_POST: 'EFFECTA_COSTS_EFFECTA_EX_POST',
    EFFECTA_CRYPTOREGISTRY_TOS: 'EFFECTA_CRYPTOREGISTRY_TOS',
    EFFECTA_CUSTOMER_INFO: 'EFFECTA_CUSTOMER_INFO',
    EFFECTA_DOCUMENT: 'EFFECTA_DOCUMENT',
    EFFECTA_INVESTOR_BROCHURE: 'EFFECTA_INVESTOR_BROCHURE',
    EFFECTA_LEGITIMATION: 'EFFECTA_LEGITIMATION',
    EFFECTA_LIST_OF_SHAREHOLDERS: 'EFFECTA_LIST_OF_SHAREHOLDERS',
    EFFECTA_LOAN_CONDITIONS: 'EFFECTA_LOAN_CONDITIONS',
    EFFECTA_NON_ASSESSMENT_CERTIFICATE: 'EFFECTA_NON_ASSESSMENT_CERTIFICATE',
    EFFECTA_PARTICIPATION_RIGHT_CONDITIONS: 'EFFECTA_PARTICIPATION_RIGHT_CONDITIONS',
    EFFECTA_PROFESSIONAL_INVESTOR_CLASSIFICATION: 'EFFECTA_PROFESSIONAL_INVESTOR_CLASSIFICATION',
    EFFECTA_PROFESSIONAL_INVESTOR_OMISSION_WPHG: 'EFFECTA_PROFESSIONAL_INVESTOR_OMISSION_WPHG',
    EFFECTA_SELF_DISCLOSURE: 'EFFECTA_SELF_DISCLOSURE',
    EFFECTA_TERMS_AND_CONDITIONS: 'EFFECTA_TERMS_AND_CONDITIONS',
    EFFECTA_TOS: 'EFFECTA_TOS',
    EFFECTA_TRANSPARANCY_REGISTER: 'EFFECTA_TRANSPARANCY_REGISTER',
    EFFECTA_UNSUITABILITY: 'EFFECTA_UNSUITABILITY',
    EFFECTA_VIB: 'EFFECTA_VIB',
    EFFECTA_WAIVER: 'EFFECTA_WAIVER',
    EFFECTA_WIB: 'EFFECTA_WIB',
    EFFECTA_ZEICHNUNGSSCHEIN: 'EFFECTA_ZEICHNUNGSSCHEIN',
    EMAIL: 'EMAIL',
    EWPG_REGISTRY_STATEMENT: 'EWPG_REGISTRY_STATEMENT',
    IMAGE: 'IMAGE',
    INCOME_STATEMENT: 'INCOME_STATEMENT',
    INVESTMENT_AGREEMENT: 'INVESTMENT_AGREEMENT',
    LEGITIMATION_PROTOCOL: 'LEGITIMATION_PROTOCOL',
    LIMITED_PARTNERSHIP_AGREEMENT: 'LIMITED_PARTNERSHIP_AGREEMENT',
    NOT_SPECIFIED: 'NOT_SPECIFIED',
    OTHER: 'OTHER',
    PAYMENT_AGB: 'PAYMENT_AGB',
    QUESTIONNAIRE: 'QUESTIONNAIRE',
    QUESTIONNAIRE_WPHG: 'QUESTIONNAIRE_WPHG',
    RECEIPT_SECONDARY_MARKET: 'RECEIPT_SECONDARY_MARKET',
    SECURITY_PROSPECTUS: 'SECURITY_PROSPECTUS',
    SELF_DISCLOSURE: 'SELF_DISCLOSURE',
    SHAREHOLDERS: 'SHAREHOLDERS',
    SURVEY_SUMMARY: 'SURVEY_SUMMARY',
    TAX_STATEMENT: 'TAX_STATEMENT',
    TRADE_COPY: 'TRADE_COPY',
    UNSUITABILITY: 'UNSUITABILITY',
    VIB: 'VIB',
    WAIVER: 'WAIVER',
    WIB: 'WIB'
} as const;
export type AdminInvestorProductDocumentsValuesRetrieveDocumentTypeEnum = typeof AdminInvestorProductDocumentsValuesRetrieveDocumentTypeEnum[keyof typeof AdminInvestorProductDocumentsValuesRetrieveDocumentTypeEnum];
/**
 * @export
 */
export const AdminInvestorProductDocumentsValuesRetrieveOrderingEnum = {
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    DOCUMENT_NAME_DESC: '-document_name',
    DOCUMENT_NAME_ASC: 'document_name',
    DOCUMENT_TYPE_DESC: '-document_type',
    DOCUMENT_TYPE_ASC: 'document_type',
    ID_DESC: '-id',
    ID_ASC: 'id',
    INVESTOR_ID_DESC: '-investor_id',
    INVESTOR_ID_ASC: 'investor_id',
    INVESTOR_NAME_DESC: '-investor_name',
    INVESTOR_NAME_ASC: 'investor_name',
    PRODUCT_ID_DESC: '-product_id',
    PRODUCT_ID_ASC: 'product_id',
    PRODUCT_NAME_DESC: '-product_name',
    PRODUCT_NAME_ASC: 'product_name',
    UPDATED_AT_DESC: '-updated_at',
    UPDATED_AT_ASC: 'updated_at'
} as const;
export type AdminInvestorProductDocumentsValuesRetrieveOrderingEnum = typeof AdminInvestorProductDocumentsValuesRetrieveOrderingEnum[keyof typeof AdminInvestorProductDocumentsValuesRetrieveOrderingEnum];
